import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  MessageCircle, Send, X, Minus, PanelRight, Maximize2, Phone, PhoneOff,
  Mic, MicOff, Pause, Play, ExternalLink, ChevronDown, Plus, Search, ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCall } from "../layout/ActiveCallOverlay";
import TeamOnCallStrip from "./TeamOnCallStrip";

const API = process.env.REACT_APP_BACKEND_URL;
const MODE_KEY = "avida.comms-dock.mode";       // 'collapsed' | 'floating' | 'rail'
const ACTIVE_KEY = "avida.comms-dock.active";   // last open conversation id
const DRAFT_KEY = "avida.comms-dock.drafts";    // { [convoId]: text }
const RAIL_WIDTH = 372;

const NAVY = "#003366";
const NAVY_GRAD = "linear-gradient(135deg, #003366 0%, #00467a 100%)";
const YELLOW = "#FFDA00";

const tok = () => localStorage.getItem("token") || "";
// Auth rides on the httpOnly session cookie (DEF-23) — send credentials on
// every request; the Bearer header is only a harmless legacy fallback.
const fetchOpts = () => {
  const t = tok();
  const o = { credentials: "include" };
  if (t) o.headers = { Authorization: `Bearer ${t}` };
  return o;
};

function convoTitle(c) {
  if (!c) return "Chat";
  if (c.name) return c.name;
  if (c.other_user) return `${c.other_user.first_name || ""} ${c.other_user.last_name || ""}`.trim() || "Direct message";
  return (c.id || "").slice(0, 6);
}
function initials(name) {
  return (name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}
function fmtDuration(s) {
  const m = Math.floor((s || 0) / 60);
  const sec = (s || 0) % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const STATUS_OPTIONS = [
  { value: "available", label: "Available", dot: "#22c55e" },
  { value: "busy", label: "Busy", dot: "#ef4444" },
  { value: "away", label: "Away", dot: "#f59e0b" },
];

export default function CommsDock() {
  const { user, updateStatus } = useAuth();
  const call = useCall() || {};
  const navigate = useNavigate();

  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "collapsed");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(ACTIVE_KEY) || null);
  const [messagesByConvo, setMessagesByConvo] = useState({});
  const [drafts, setDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; }
  });
  const [onlineUsers, setOnlineUsers] = useState({});
  const [unreadByConvo, setUnreadByConvo] = useState({});
  const [statusOpen, setStatusOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [pickerUsers, setPickerUsers] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerLoading, setPickerLoading] = useState(false);

  const wsRef = useRef(null);
  const activeIdRef = useRef(activeId);
  const modeRef = useRef(mode);
  const scrollRef = useRef(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  const expanded = mode === "floating" || mode === "rail";

  // ── Persist light state ──────────────────────────────────────
  useEffect(() => { localStorage.setItem(MODE_KEY, mode); }, [mode]);
  useEffect(() => { if (activeId) localStorage.setItem(ACTIVE_KEY, activeId); }, [activeId]);
  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts)); }, [drafts]);

  // Let the global FloatingChats widget skip its pop-up toast for the
  // conversation the dock is actively showing (no double notification).
  useEffect(() => {
    window.__comms_dock_open_convo = expanded ? activeId : null;
    return () => { window.__comms_dock_open_convo = null; };
  }, [expanded, activeId]);

  // ── Split-rail pushes page content left via body padding ─────
  useEffect(() => {
    if (mode === "rail" && window.innerWidth >= 1024) {
      document.body.style.transition = "padding-right 0.25s ease";
      document.body.style.paddingRight = `${RAIL_WIDTH}px`;
    } else {
      document.body.style.paddingRight = "";
    }
    return () => { document.body.style.paddingRight = ""; };
  }, [mode]);

  // ── Load conversations ───────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/im/conversations`, fetchOpts());
      if (!r.ok) return;
      const list = await r.json();
      if (Array.isArray(list)) {
        setConversations(list);
        // Reconcile unread from the server, but keep the currently-open thread
        // at 0 (we mark it read on open). This keeps the collapsed-tab nudge
        // accurate even when the WebSocket is down (poll-driven).
        setUnreadByConvo((prev) => {
          const next = { ...prev };
          list.forEach((c) => {
            if (c.unread_count == null) return;
            const activeOpen = c.id === activeIdRef.current && modeRef.current !== "collapsed";
            next[c.id] = activeOpen ? 0 : c.unread_count;
          });
          return next;
        });
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadConversations();
    const iv = setInterval(loadConversations, 15000);
    return () => clearInterval(iv);
  }, [user, loadConversations]);

  // ── Load a thread's messages + mark read ─────────────────────
  // Always (re)fetch on open: the non-paginated GET marks the conversation read
  // server-side (background task), so read-state syncs even without the WS.
  const openConversation = useCallback(async (id) => {
    setActiveId(id);
    setUnreadByConvo((prev) => ({ ...prev, [id]: 0 }));
    try {
      const r = await fetch(`${API}/api/im/conversations/${id}/messages?limit=50`, fetchOpts());
      const data = r.ok ? await r.json() : [];
      const msgs = Array.isArray(data) ? data : (data.items || []);
      setMessagesByConvo((prev) => ({ ...prev, [id]: msgs }));
    } catch { /* noop */ }
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "read", conversation_id: id }));
      }
    } catch { /* noop */ }
  }, []);

  // ── WebSocket: shared, auto-reconnecting (backend allows many sockets/user) ──
  useEffect(() => {
    if (!user) return;
    let alive = true;
    let reconnect = null;
    let heartbeat = null;
    let attempts = 0;
    let lastPong = Date.now();

    const connect = () => {
      if (!alive) return;
      const wsUrl = API.replace(/^http/, "ws");
      // Cookie-authenticated WS (same pattern as FloatingChats) — the browser
      // sends the httpOnly access_token cookie automatically on same-origin WS.
      const ws = new WebSocket(`${wsUrl}/api/im/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        attempts = 0; lastPong = Date.now();
        loadConversations();  // (re)load list once the socket is up; fixes the empty-list race
        if (heartbeat) clearInterval(heartbeat);
        heartbeat = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          if (Date.now() - lastPong > 75000) { try { ws.close(); } catch { /* */ } return; }
          try { ws.send(JSON.stringify({ type: "ping", ts: Date.now() })); } catch { /* */ }
        }, 25000);
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "pong") { lastPong = Date.now(); return; }
          if (data.type === "presence") {
            setOnlineUsers((prev) => ({ ...prev, [data.user_id]: !!data.online }));
            return;
          }
          if (data.type === "new_conversation") { loadConversations(); return; }
          if (data.type === "message" && data.message) {
            const msg = data.message;
            const cid = msg.conversation_id;
            setMessagesByConvo((prev) => {
              const cur = prev[cid] || [];
              if (msg.id && cur.some((m) => m.id === msg.id)) return prev;
              return { ...prev, [cid]: [...cur, msg].slice(-200) };
            });
            // bump convo to top
            setConversations((prev) => {
              const idx = prev.findIndex((c) => c.id === cid);
              if (idx === -1) { loadConversations(); return prev; }
              const copy = [...prev];
              const [c] = copy.splice(idx, 1);
              return [c, ...copy];
            });
            const isMine = msg.sender_id === user.id;
            const isViewing = modeRef.current !== "collapsed" && activeIdRef.current === cid;
            if (!isMine && !isViewing) {
              setUnreadByConvo((prev) => ({ ...prev, [cid]: (prev[cid] || 0) + 1 }));
            } else if (!isMine && isViewing && wsRef.current?.readyState === WebSocket.OPEN) {
              try { wsRef.current.send(JSON.stringify({ type: "read", conversation_id: cid })); } catch { /* */ }
            }
          }
        } catch { /* noop */ }
      };

      ws.onclose = (ev) => {
        wsRef.current = null;
        if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
        if (!alive || ev?.code === 4001) return;
        const delay = Math.min(1000 * Math.pow(2, Math.min(attempts, 5)), 15000);
        attempts += 1;
        reconnect = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* */ } };
    };

    connect();
    return () => {
      alive = false;
      if (reconnect) clearTimeout(reconnect);
      if (heartbeat) clearInterval(heartbeat);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* */ } }
    };
  }, [user, loadConversations]);

  // Auto-scroll active thread
  const activeMessages = messagesByConvo[activeId] || [];
  useEffect(() => {
    if (scrollRef.current && expanded) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeMessages, expanded, activeId]);

  const totalUnread = useMemo(
    () => Object.values(unreadByConvo).reduce((a, b) => a + (b || 0), 0),
    [unreadByConvo]
  );

  // ── Unread nudge: briefly "pop" the collapsed tab when unread goes UP ──
  const prevUnreadRef = useRef(0);
  useEffect(() => {
    if (totalUnread > prevUnreadRef.current) {
      setNudge(true);
      const t = setTimeout(() => setNudge(false), 2600);
      prevUnreadRef.current = totalUnread;
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = totalUnread;
  }, [totalUnread]);

  // ── Live-receive fallback: poll the OPEN thread so new messages appear even
  //    if the WebSocket is down (merge-only, so it never yanks the scroll). ──
  useEffect(() => {
    if (!activeId || !expanded) return;
    let on = true;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/im/conversations/${activeId}/messages?limit=50`, fetchOpts());
        if (!r.ok || !on) return;
        const data = await r.json();
        const msgs = Array.isArray(data) ? data : (data.items || []);
        setMessagesByConvo((prev) => {
          const cur = prev[activeId] || [];
          const curIds = new Set(cur.map((m) => m.id));
          const added = msgs.filter((m) => m.id && !curIds.has(m.id));
          if (added.length === 0) return prev;   // nothing new → no re-render/scroll
          return { ...prev, [activeId]: [...cur, ...added].slice(-200) };
        });
      } catch { /* noop */ }
    };
    const iv = setInterval(poll, 6000);
    return () => { on = false; clearInterval(iv); };
  }, [activeId, expanded]);

  // ── Start a brand-new direct chat from inside the dock ──
  const openNewChat = useCallback(async () => {
    setNewChatOpen(true); setPickerSearch(""); setPickerLoading(true);
    try {
      const r = await fetch(`${API}/api/im/users`, fetchOpts());
      const list = r.ok ? await r.json() : [];
      setPickerUsers(Array.isArray(list) ? list : []);
    } catch { /* noop */ } finally { setPickerLoading(false); }
  }, []);

  const startChat = useCallback(async (u) => {
    try {
      const r = await fetch(`${API}/api/im/conversations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "direct", participants: [u.id] }),
      });
      if (!r.ok) return;
      const convo = await r.json();
      setNewChatOpen(false); setPickerSearch("");
      await loadConversations();        // pull the enriched convo (name/other_user)
      openConversation(convo.id);
    } catch { /* noop */ }
  }, [loadConversations, openConversation]);

  const sendMessage = useCallback(async () => {
    const text = (drafts[activeId] || "").trim();
    if (!text || !activeId) return;
    setDrafts((prev) => ({ ...prev, [activeId]: "" }));
    // Send over REST (cookie auth, reliable through the preview proxy). The
    // backend also broadcasts over WS, so other tabs get it live; our own WS
    // echo is de-duped by message id. WS stays for RECEIVING.
    try {
      const r = await fetch(`${API}/api/im/conversations/${activeId}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) throw new Error(`send failed ${r.status}`);
      const msg = await r.json();
      setMessagesByConvo((prev) => {
        const cur = prev[activeId] || [];
        if (msg.id && cur.some((m) => m.id === msg.id)) return prev;
        return { ...prev, [activeId]: [...cur, msg].slice(-200) };
      });
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === activeId);
        if (idx === -1) return prev;
        const copy = [...prev];
        const [c] = copy.splice(idx, 1);
        return [c, ...copy];
      });
    } catch {
      setDrafts((prev) => ({ ...prev, [activeId]: text }));  // restore draft on failure
    }
  }, [drafts, activeId]);

  if (!user) return null;

  const activeConvo = conversations.find((c) => c.id === activeId) || null;
  const myStatus = STATUS_OPTIONS.find((s) => s.value === (user.status || "available")) || STATUS_OPTIONS[0];
  const hasCall = !!(call.activeCall || call.incomingCall);

  // ── Collapsed launcher — right-edge tab (clears the crowded bottom-right
  //    corner: Ask-Avi FAB, clock-in card, floating chat bubbles) ──
  if (!expanded) {
    const nudging = totalUnread > 0;
    return (
      <button
        type="button"
        onClick={() => setMode("floating")}
        data-testid="comms-dock-pill"
        title="Open Communications"
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-[56] flex flex-col items-center gap-1.5 py-3 rounded-l-2xl shadow-2xl transition-all duration-300 ${nudging ? "ring-4 ring-yellow-300/70" : ""} ${nudge ? "pr-5 pl-3 scale-[1.14]" : "px-2 hover:px-2.5"}`}
        style={{ background: NAVY_GRAD, color: YELLOW }}
        data-nudge={nudge ? "1" : "0"}
      >
        {/* gentle pulsing glow while there are unread messages */}
        {nudging && (
          <span
            className="absolute inset-0 rounded-l-2xl animate-pulse pointer-events-none"
            style={{ boxShadow: `0 0 18px 3px ${YELLOW}` }}
          />
        )}
        <span className="relative flex items-center justify-center">
          {hasCall ? <Phone className="w-5 h-5 animate-pulse" /> : <MessageCircle className={`w-5 h-5 ${nudge ? "animate-bounce" : ""}`} />}
          {totalUnread > 0 && (
            <span
              data-testid="comms-dock-unread"
              className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
              style={{ background: "#ef4444", color: "#fff" }}
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </span>
        <span className="relative text-[10px] font-bold tracking-wide" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          COMMS
        </span>
        <span className="relative w-2.5 h-2.5 rounded-full border border-white/40" style={{ background: myStatus.dot }} title={myStatus.label} />
      </button>
    );
  }

  // ── Expanded (floating or rail) ──────────────────────────────
  const shell =
    mode === "rail"
      ? "fixed top-0 right-0 h-screen z-[55] flex flex-col bg-white border-l border-slate-200 shadow-2xl"
      : "fixed bottom-4 right-4 z-[55] flex flex-col bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden";
  const shellStyle = mode === "rail" ? { width: RAIL_WIDTH } : { width: 660, height: 560 };

  return (
    <div className={shell} style={shellStyle} data-testid="comms-dock-panel">
      {/* New-chat picker overlay */}
      {newChatOpen && (
        <div className="absolute inset-0 z-20 bg-white flex flex-col rounded-2xl overflow-hidden" data-testid="comms-dock-new-chat-panel">
          <div className="flex items-center gap-2 px-3 py-2.5 shrink-0" style={{ background: NAVY_GRAD }}>
            <button type="button" onClick={() => setNewChatOpen(false)} data-testid="comms-dock-new-chat-back" className="p-1 rounded hover:bg-white/10 text-white/90">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-bold text-white flex-1">New message</span>
            <button type="button" onClick={() => setNewChatOpen(false)} className="p-1 rounded hover:bg-white/10 text-white/80">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-2 shrink-0 border-b border-slate-100">
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-slate-100">
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder="Search teammates…"
                data-testid="comms-dock-new-chat-search"
                className="flex-1 bg-transparent text-[12px] focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {pickerLoading ? (
              <p className="px-3 py-4 text-[12px] text-slate-400">Loading teammates…</p>
            ) : (() => {
              const q = pickerSearch.trim().toLowerCase();
              const filtered = pickerUsers.filter((u) =>
                !q ||
                `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q)
              );
              if (filtered.length === 0) return <p className="px-3 py-4 text-[12px] text-slate-400">No teammates found.</p>;
              return filtered.map((u) => {
                const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => startChat(u)}
                    data-testid={`comms-dock-new-chat-user-${u.id}`}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="relative w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: YELLOW, color: NAVY }}>
                      {initials(name)}
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white" style={{ background: u.online ? "#22c55e" : "#cbd5e1" }} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[12px] font-semibold text-slate-700 truncate">{name}</span>
                      <span className="block text-[10px] text-slate-400 truncate capitalize">{(u.role || "").replace(/_/g, " ")}</span>
                    </span>
                  </button>
                );
              });
            })()}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 shrink-0" style={{ background: NAVY_GRAD }}>
        <MessageCircle className="w-4 h-4" style={{ color: YELLOW }} />
        <span className="text-sm font-bold text-white flex-1">Communications</span>

        {/* Presence status */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setStatusOpen((v) => !v)}
            data-testid="comms-dock-status"
            className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/10 text-white/90 text-xs"
          >
            <span className="w-2 h-2 rounded-full" style={{ background: myStatus.dot }} />
            {myStatus.label}
            <ChevronDown className="w-3 h-3" />
          </button>
          {statusOpen && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-10">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => { updateStatus(s.value); setStatusOpen(false); }}
                  data-testid={`comms-dock-status-${s.value}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: s.dot }} />
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMode(mode === "rail" ? "floating" : "rail")}
          data-testid="comms-dock-rail-toggle"
          title={mode === "rail" ? "Float panel" : "Dock to side (split view)"}
          className="p-1.5 rounded hover:bg-white/10 text-white/80"
        >
          {mode === "rail" ? <Maximize2 className="w-4 h-4" /> : <PanelRight className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => setMode("collapsed")}
          data-testid="comms-dock-collapse"
          title="Minimize"
          className="p-1.5 rounded hover:bg-white/10 text-white/80"
        >
          <Minus className="w-4 h-4" />
        </button>
      </div>

      {/* Live call bar */}
      {hasCall && <CallBar call={call} />}

      {/* Dispatch: who's on a call right now (hidden when nobody is) */}
      <TeamOnCallStrip compact />

      {/* Body: conversation list + thread */}
      <div className="flex-1 flex min-h-0">
        {/* Conversation list */}
        <div className="w-[190px] shrink-0 border-r border-slate-200 flex flex-col min-h-0">
          <div className="px-3 py-2 flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Chats</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={openNewChat}
                title="New chat"
                data-testid="comms-dock-new-chat"
                className="text-slate-400 hover:text-[#003366]"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/messages")}
                title="Open full Messages"
                data-testid="comms-dock-open-full"
                className="text-slate-400 hover:text-slate-600"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <p className="px-3 py-4 text-[12px] text-slate-400">No conversations yet.</p>
            ) : (
              conversations.map((c) => {
                const title = convoTitle(c);
                const unread = unreadByConvo[c.id] || 0;
                const online = c.other_user ? !!onlineUsers[c.other_user.id] : false;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => openConversation(c.id)}
                    data-testid={`comms-dock-convo-${c.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 ${activeId === c.id ? "bg-slate-100" : ""}`}
                  >
                    <span className="relative w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: YELLOW, color: NAVY }}>
                      {initials(title)}
                      {c.other_user && (
                        <span
                          className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
                          style={{ background: online ? "#22c55e" : "#cbd5e1" }}
                        />
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[12px] font-semibold text-slate-700 truncate">{title}</span>
                      <span className="block text-[10px] text-slate-400 truncate">{c.type === "direct" ? "Direct" : "Channel"}</span>
                    </span>
                    {unread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "#ef4444", color: "#fff" }}>
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread */}
        <div className="flex-1 flex flex-col min-h-0">
          {!activeConvo ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-300 p-6 text-center">
              <MessageCircle className="w-10 h-10 mb-2" />
              <p className="text-sm">Pick a conversation to keep it open while you work.</p>
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b border-slate-100 shrink-0">
                <p className="text-[13px] font-bold text-slate-800 truncate">{convoTitle(activeConvo)}</p>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 bg-slate-50">
                {activeMessages.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-slate-300 text-xs">No messages yet</div>
                ) : (
                  activeMessages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id || m.client_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className="max-w-[78%] px-2.5 py-1.5 rounded-2xl text-[12px] leading-snug break-words"
                          style={{
                            background: mine ? NAVY_GRAD : "#fff",
                            color: mine ? "#fff" : "#1e293b",
                            border: mine ? `1px solid ${NAVY}` : "1px solid #e2e8f0",
                            borderTopRightRadius: mine ? 4 : 16,
                            borderTopLeftRadius: mine ? 16 : 4,
                          }}
                        >
                          {!mine && activeConvo.type !== "direct" && (
                            <p className="text-[9px] font-semibold mb-0.5" style={{ color: NAVY }}>{m.sender_name}</p>
                          )}
                          {m.text && <span>{m.text}</span>}
                          {m.attachment && (
                            <a
                              href={`${API}/api/im/files/${m.attachment.storage_path}?auth=${tok()}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block mt-1 underline text-[11px] opacity-90"
                            >
                              {m.attachment.filename || "Attachment"}
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-slate-200 p-2 bg-white flex items-center gap-2 shrink-0">
                <input
                  type="text"
                  value={drafts[activeId] || ""}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [activeId]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Type a message…"
                  data-testid="comms-dock-message-input"
                  className="flex-1 text-[12px] px-3 py-2 rounded-full bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!(drafts[activeId] || "").trim()}
                  data-testid="comms-dock-send"
                  className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-30 shrink-0"
                  style={{ background: NAVY, color: YELLOW }}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CallBar({ call }) {
  const { activeCall, incomingCall, callDuration, isMuted, isOnHold, toggleMute, toggleHold, endCall, answerCall, rejectCall } = call;

  if (incomingCall && !activeCall) {
    const from = incomingCall.from || incomingCall.parameters?.From || "Unknown";
    return (
      <div className="flex items-center gap-2 px-3 py-2 shrink-0" style={{ background: "#064e3b" }} data-testid="comms-dock-callbar">
        <Phone className="w-4 h-4 text-emerald-300 animate-pulse" />
        <span className="flex-1 min-w-0">
          <span className="block text-[12px] font-bold text-white truncate">Incoming call</span>
          <span className="block text-[10px] text-emerald-200 truncate">{from}</span>
        </span>
        <button type="button" onClick={answerCall} data-testid="comms-dock-call-answer" className="px-2.5 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-semibold flex items-center gap-1">
          <Phone className="w-3.5 h-3.5" /> Answer
        </button>
        <button type="button" onClick={rejectCall} data-testid="comms-dock-call-reject" className="p-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white">
          <PhoneOff className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  if (!activeCall) return null;
  const who = activeCall.direction === "inbound" ? activeCall.from : activeCall.to;
  return (
    <div className="flex items-center gap-2 px-3 py-2 shrink-0" style={{ background: NAVY }} data-testid="comms-dock-callbar">
      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
      <span className="flex-1 min-w-0">
        <span className="block text-[12px] font-bold text-white truncate">{who || "On a call"}</span>
        <span className="block text-[10px] text-blue-200">{isOnHold ? "On hold" : fmtDuration(callDuration)}</span>
      </span>
      <button type="button" onClick={toggleMute} data-testid="comms-dock-call-mute" title={isMuted ? "Unmute" : "Mute"} className={`p-1.5 rounded-full ${isMuted ? "bg-amber-400 text-slate-900" : "bg-white/15 text-white hover:bg-white/25"}`}>
        {isMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
      </button>
      <button type="button" onClick={toggleHold} data-testid="comms-dock-call-hold" title={isOnHold ? "Resume" : "Hold"} className={`p-1.5 rounded-full ${isOnHold ? "bg-amber-400 text-slate-900" : "bg-white/15 text-white hover:bg-white/25"}`}>
        {isOnHold ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
      </button>
      <button type="button" onClick={endCall} data-testid="comms-dock-call-end" title="Hang up" className="p-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white">
        <PhoneOff className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
